/// <reference lib="dom" />
/**
 * Canvas2D draw helpers for distant celestial objects: planet and moon.
 * Extracted from webSkyDraw.web.ts to keep files under the 300-line limit.
 *
 * Web-only (.web.ts) — never bundled on native. Read-only, no gameState mutation.
 *
 * @module webCelestialDraw
 */

import { VIEWPORT_HEIGHT } from '../../constants/gameConstants';

// ── Moon explosion state ─────────────────────────────────────────────────────

interface MoonExplosion {
  angle: number;
  dist: number;
  phase: number;
  speed: number;
  size: number;
}

let _moonExplosions: MoonExplosion[] = [];

/**
 * Initialise moon surface explosions.
 * @param rand - Deterministic PRNG function returning 0..1.
 */
export function initMoonExplosions(rand: () => number): void {
  _moonExplosions = [];
  for (let i = 0; i < 6; i++) {
    _moonExplosions.push({
      angle: rand() * Math.PI * 2,
      dist: 0.5 + rand() * 0.35,
      phase: rand() * Math.PI * 2,
      speed: 0.3 + rand() * 0.5,
      size: 3 + rand() * 4,
    });
  }
}

/**
 * Draw distant sky objects: moon and planet. Called between mid and near
 * parallax layers so the near hills overlap these objects.
 *
 * @param ctx     - Canvas 2D rendering context.
 * @param camX    - Horizontal camera offset in world pixels.
 * @param skyTime - Cumulative sky animation time in seconds.
 */
export function drawCelestials(
  ctx: CanvasRenderingContext2D,
  camX: number,
  skyTime: number,
): void {
  // DECISION: Planet top-left, drawn between mid/near parallax. Slow 0.01 parallax.
  const planetX = 80 - camX * 0.01;
  const planetY = 50;
  const planetR = 35;

  // Planet atmosphere glow
  const atmoGlow = ctx.createRadialGradient(planetX, planetY, planetR * 0.8, planetX, planetY, planetR * 1.8);
  atmoGlow.addColorStop(0, 'rgba(120,80,180,0.2)');
  atmoGlow.addColorStop(1, 'rgba(120,80,180,0)');
  ctx.fillStyle = atmoGlow;
  ctx.fillRect(planetX - planetR * 2, planetY - planetR * 2, planetR * 4, planetR * 4);

  // Planet body — solid purple/blue gas giant
  const planetGrad = ctx.createRadialGradient(planetX - planetR * 0.3, planetY - planetR * 0.2, 0, planetX, planetY, planetR);
  planetGrad.addColorStop(0, 'rgba(150,110,210,0.9)');
  planetGrad.addColorStop(0.5, 'rgba(100,65,160,0.85)');
  planetGrad.addColorStop(1, 'rgba(55,35,110,0.75)');
  ctx.fillStyle = planetGrad;
  ctx.beginPath();
  ctx.arc(planetX, planetY, planetR, 0, Math.PI * 2);
  ctx.fill();

  // Planet bands (horizontal stripes for gas giant look)
  ctx.save();
  ctx.beginPath();
  ctx.arc(planetX, planetY, planetR, 0, Math.PI * 2);
  ctx.clip();
  for (let i = -3; i <= 3; i++) {
    const bandY = planetY + i * planetR * 0.28;
    ctx.fillStyle = i % 2 === 0 ? 'rgba(170,130,230,0.15)' : 'rgba(70,40,120,0.12)';
    ctx.fillRect(planetX - planetR, bandY - 2, planetR * 2, 4);
  }
  ctx.restore();

  // Planet ring (tilted ellipse)
  ctx.strokeStyle = 'rgba(190,160,240,0.4)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.ellipse(planetX, planetY + 2, planetR * 1.6, planetR * 0.25, -0.15, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = 'rgba(210,180,250,0.25)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.ellipse(planetX, planetY + 2, planetR * 1.75, planetR * 0.3, -0.15, 0, Math.PI * 2);
  ctx.stroke();

  // DECISION: Moon drawn before mid+near parallax so hills overlap it.
  const moonX = 480 * 0.6 - camX * 0.015;
  const moonY = VIEWPORT_HEIGHT * 0.55;
  const moonR = 65;

  // Moon glow
  const moonGlow = ctx.createRadialGradient(moonX, moonY, moonR * 0.5, moonX, moonY, moonR * 2);
  moonGlow.addColorStop(0, 'rgba(220,220,240,0.12)');
  moonGlow.addColorStop(1, 'rgba(220,220,240,0)');
  ctx.fillStyle = moonGlow;
  ctx.fillRect(moonX - moonR * 2.5, moonY - moonR * 2.5, moonR * 5, moonR * 5);

  // Moon body — fully opaque for a solid, imposing look
  const moonGrad = ctx.createRadialGradient(moonX - moonR * 0.2, moonY - moonR * 0.2, 0, moonX, moonY, moonR);
  moonGrad.addColorStop(0, '#E8E8F5');
  moonGrad.addColorStop(0.6, '#C8C8DC');
  moonGrad.addColorStop(1, '#A0A0B8');
  ctx.fillStyle = moonGrad;
  ctx.beginPath();
  ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
  ctx.fill();

  // Moon rim shadow — dark edge for a solid 3D look
  ctx.strokeStyle = 'rgba(80,80,100,0.4)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
  ctx.stroke();

  // Moon craters
  ctx.save();
  ctx.beginPath();
  ctx.arc(moonX, moonY, moonR, 0, Math.PI * 2);
  ctx.clip();
  const craters = [
    [-0.3, -0.25, 8], [0.2, -0.1, 12], [-0.1, 0.3, 6],
    [0.35, 0.25, 9], [-0.4, 0.1, 5], [0.1, -0.4, 7],
  ];
  for (const [cx, cy, cr] of craters) {
    ctx.fillStyle = 'rgba(130,130,155,0.35)';
    ctx.beginPath();
    ctx.arc(moonX + cx * moonR, moonY + cy * moonR, cr, 0, Math.PI * 2);
    ctx.fill();
  }

  // Moon explosions — flashes on the surface
  for (const exp of _moonExplosions) {
    const pulse = (Math.sin(skyTime * exp.speed + exp.phase) + 1) * 0.5;
    if (pulse < 0.3) continue;
    const ex = moonX + Math.cos(exp.angle) * exp.dist * moonR;
    const ey = moonY + Math.sin(exp.angle) * exp.dist * moonR;
    const er = exp.size * pulse;

    const expGlow = ctx.createRadialGradient(ex, ey, 0, ex, ey, er * 2.5);
    expGlow.addColorStop(0, `rgba(255,200,80,${(0.5 * pulse).toFixed(2)})`);
    expGlow.addColorStop(0.5, `rgba(255,120,40,${(0.25 * pulse).toFixed(2)})`);
    expGlow.addColorStop(1, 'rgba(255,80,20,0)');
    ctx.fillStyle = expGlow;
    ctx.fillRect(ex - er * 3, ey - er * 3, er * 6, er * 6);

    ctx.fillStyle = `rgba(255,240,200,${(0.7 * pulse).toFixed(2)})`;
    ctx.beginPath();
    ctx.arc(ex, ey, Math.max(0.5, er * 0.6), 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}
